import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  useTheme,
  alpha,
} from "@mui/material";
import { Add as AddIcon, Visibility, VisibilityOff } from "@mui/icons-material";
import { validateCategoryName } from "../../utils/categoryValidation";

interface Props {
  onAdd: (name: string) => void;
  showInactive: boolean;
  onToggleShowInactive: () => void;
  error?: string | null;
  onClearError?: () => void;
  typeLabel: string;
}

const MAX_LENGTH = 30;

const AddCategoryFormComponent: React.FC<Props> = ({
  onAdd,
  showInactive,
  onToggleShowInactive,
  error,
  onClearError,
  typeLabel,
}) => {
  const theme = useTheme();
  const [newCategory, setNewCategory] = useState("");

  const handleAdd = () => {
    const trimmed = newCategory.trim();
    const error = validateCategoryName(trimmed, MAX_LENGTH);
    if (error) return;
    
    onAdd(trimmed);
    setNewCategory("");
    if (onClearError) onClearError();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && newCategory.trim()) {
      handleAdd();
    }
  };

  const isValid = newCategory.trim().length > 0 && newCategory.length <= MAX_LENGTH;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {error && (
        <Alert
          severity="warning"
          sx={{
            borderRadius: 2,
            py: 0.4,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
          }}
        >
          {error}
        </Alert>
      )}

      {/* Add Category Input Row */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, flexDirection: { xs: "column", sm: "row" } }}>
        <TextField
          label={`Add ${typeLabel} Category`}
          variant="outlined"
          size="small"
          value={newCategory}
          onChange={(e) => {
            if (e.target.value.length <= MAX_LENGTH) {
              setNewCategory(e.target.value);
              if (error && onClearError) onClearError();
            }
          }}
          onKeyPress={handleKeyPress}
          helperText={`${newCategory.length}/${MAX_LENGTH} characters`}
          inputProps={{ maxLength: MAX_LENGTH }}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.75rem',
              color: newCategory.length > MAX_LENGTH * 0.8 
                ? theme.palette.warning.main 
                : theme.palette.text.secondary,
            },
          }}
        />
        
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!isValid}
          startIcon={<AddIcon />}
          sx={{
            minWidth: 'auto',
            px: 2.2,
            py: 1,
            width: { xs: "100%", sm: "auto" },
            borderRadius: 2,
            fontWeight: 600,
            background: isValid 
              ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
              : theme.palette.action.disabledBackground,
            '&:hover': {
              background: isValid 
                ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                : theme.palette.action.disabledBackground,
              transform: isValid ? 'translateY(-1px)' : 'none',
              boxShadow: isValid 
                ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`
                : 'none',
            },
            '&:disabled': {
              color: theme.palette.action.disabled,
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          Add
        </Button>
      </Box>

      {/* Show Inactive Toggle */}
      <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
        <FormControlLabel
          control={
            <Switch 
              checked={showInactive} 
              onChange={onToggleShowInactive}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showInactive ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
              Show Inactive
            </Box>
          }
          sx={{
            margin: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: '0.875rem',
              fontWeight: 500,
              color: theme.palette.text.secondary,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default AddCategoryFormComponent;
